import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router";
import { useEndpoints } from "../api/ApiContext";
import { ApiError, PASSWORD_POLICY_MESSAGE } from "../api/errors";
import type { PartyType } from "../api/types";
import { Banner, Button, Input, RadioGroup } from "../components/ui";
import { checkPasswordLocally } from "../lib/passwordPolicy";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "./AuthProvider";
import { isStaffReviewer, PlatformAccountNotice, STAFF_HOME } from "./guards";
import { LoginFlow } from "./LoginFlow";
import { NewPasswordField } from "./NewPasswordField";
import { RateLimitNotice, useRateLimit } from "./rateLimit";

export function homeFor(partyType: PartyType): string {
  return partyType === "advertiser" ? "/advertiser" : "/publisher";
}

/** Only "advertiser"/"publisher" pre-select the signup radio; anything else (including absent) leaves it unset. */
function partyTypeFromQuery(value: string | null): PartyType | "" {
  return value === "advertiser" || value === "publisher" ? value : "";
}

export function LoginPage() {
  const { state, completeLogin } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  if (state.status === "platform") {
    if (!isStaffReviewer(state.me)) return <PlatformAccountNotice />;
    return <Navigate to={from && from.startsWith(STAFF_HOME) ? from : STAFF_HOME} replace />;
  }
  if (state.status === "signed_in") {
    const target = from && from.startsWith(homeFor(state.partyType)) ? from : homeFor(state.partyType);
    if (state.mfa === "not_enrolled") return <Navigate to={`${homeFor(state.partyType)}/security?setup=1`} replace />;
    return <Navigate to={target} replace />;
  }

  return (
    <AuthLayout title="Sign in" footer="Advertisers and publishers sign in here." variant="signin">
      {state.status === "signed_out" && state.reason ? <Banner tone="info">{state.reason}</Banner> : null}
      {/* On success the signed_in branch above redirects on re-render. */}
      <LoginFlow onAuthenticated={completeLogin} />
    </AuthLayout>
  );
}

/**
 * The redirect_uri registered for the OAuth client. core-platform returns
 * authorization codes in JSON, so nothing actually redirects here; anyone
 * who lands on it is sent to sign in.
 */
export function AuthCallbackPage() {
  return <Navigate to="/login" replace />;
}

// POST /v1/auth/signup (auth.go handleSignup). Creates the party (advertiser
// or publisher) and its first user. UX-DESIGN.md §2.1/§3.0's organization
// setup (billing address, tax ID) and compliance screening have no API yet
// (API-GAPS.md G-10, G-11, G-31).
//
// The password must pass internal/auth/password_policy.go (NIST SP 800-63B
// rev. 4): 15+ characters, at most 72 bytes, not common, not the email, no
// composition rules. Length and bytes are checked here for instant
// feedback; the server's `password_policy` `reason` covers the rest.
// Signup is rate limited per IP (429 + Retry-After).
export function SignupPage() {
  const ep = useEndpoints();
  const [searchParams] = useSearchParams();
  const [partyType, setPartyType] = useState<PartyType | "">(() => partyTypeFromQuery(searchParams.get("type")));
  const [legalName, setLegalName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryContact, setRecoveryContact] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const rl = useRateLimit();

  async function submit(e: FormEvent) {
    e.preventDefault();
    const fe: Record<string, string> = {};
    if (!partyType) fe.partyType = "Choose an account type.";
    if (!legalName.trim()) fe.legalName = "Enter the legal name of your business or yourself.";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) fe.email = "Enter a valid email address.";
    const local = checkPasswordLocally(password);
    if (local) fe.password = PASSWORD_POLICY_MESSAGE[local];
    if (!recoveryContact.trim()) fe.recoveryContact = "Enter a recovery contact.";
    else if (recoveryContact.trim().toLowerCase() === email.trim().toLowerCase())
      fe.recoveryContact = "Use a different contact from your sign-in email, so a single compromised inbox can't take over the account.";
    setErrors(fe);
    if (Object.keys(fe).length > 0 || !partyType) return;
    setBusy(true);
    setError(null);
    try {
      await ep.auth.signup({
        party_type: partyType,
        legal_name: legalName.trim(),
        email: email.trim(),
        password,
        recovery_contact: recoveryContact.trim(),
      });
      setDone(true);
    } catch (err) {
      if (rl.capture(err)) return;
      if (err instanceof ApiError && err.code === "password_policy") {
        setErrors({ password: err.message });
        return;
      }
      setError(
        err instanceof ApiError && err.code === "create_user_failed"
          ? "We couldn't create that account. If you already have one, sign in instead."
          : err instanceof Error
            ? err.message
            : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title="Account created">
        <Banner tone="success" title="Your account is ready">
          Sign in to continue. You'll set up two-step verification first — it's required before you can fund ads or receive payouts.
        </Banner>
        <Link className="btn btn-primary btn-block" to="/login">
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create an account"
      footer={<Link to="/login">Already have an account? Sign in</Link>}
      variant="signup"
    >
      {error ? (
        <Banner tone="error" live="alert">
          {error}
        </Banner>
      ) : null}
      <RateLimitNotice state={rl} />
      <form className="stack" onSubmit={submit} noValidate>
        <RadioGroup<PartyType>
          legend="I want to"
          name="party_type"
          value={partyType}
          onChange={setPartyType}
          options={[
            { value: "advertiser", label: "Advertise", hint: "Create ads that appear inside AI tool results." },
            { value: "publisher", label: "Publish", hint: "Show one labelled ad in your MCP tools and earn a revenue share." },
          ]}
        />
        {errors.partyType ? <div className="field-error" role="alert">{errors.partyType}</div> : null}
        <Input label="Legal name" value={legalName} onChange={(e) => setLegalName(e.target.value)} error={errors.legalName} autoComplete="organization" />
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} autoComplete="email" />
        <NewPasswordField value={password} onChange={setPassword} error={errors.password} />
        <Input
          label="Recovery contact"
          value={recoveryContact}
          onChange={(e) => setRecoveryContact(e.target.value)}
          error={errors.recoveryContact}
          hint="An email or phone number other than your sign-in email. Used to verify it's you if you lose your device, and to alert you about security changes."
        />
        <Button type="submit" block loading={busy} disabled={rl.limited}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
